import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

// ── Small reusable panel list item ────────────────────────────────────────────
const PanelItem = ({ label, meta, sub, active, onClick, img, action, compact }) => (
  <div style={{ ...S.pi, ...(compact ? S.piCompact : {}), ...(active ? S.piActive : {}), display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={onClick}>
    {img && <img src={img} alt="" style={compact ? S.selfieThumbSm : S.selfieThumb} onError={e => { e.target.style.display = 'none'; }} />}
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={S.piName}>{label}</div>
      {meta && <div style={S.piMeta}>{meta}</div>}
      {sub  && <div style={S.piSub}>{sub}</div>}
    </div>
    {action && <div onClick={e => e.stopPropagation()}>{action}</div>}
  </div>
);

// ── Collapsible sidebar section ───────────────────────────────────────────────
const SideSection = ({ title, children }) => (
  <div style={S.sideSection}>
    <div style={S.sideSectionTitle}>{title}</div>
    {children}
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
const SuperAdminInvestigate = () => {
  useAuth();

  // ── Level selections ──────────────────────────────────────────────────────
  const [selectedDealer,       setSelectedDealer]       = useState(null);
  const [dealerEventsMode,     setDealerEventsMode]     = useState(false);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [selectedEvent,        setSelectedEvent]        = useState(null);
  const [selectedVisitor,      setSelectedVisitor]      = useState(null);

  // ── Data lists ────────────────────────────────────────────────────────────
  const [dealers,           setDealers]           = useState([]);
  const [dealersLoading,    setDealersLoading]    = useState(true);
  const [level2Items,       setLevel2Items]       = useState([]);
  const [level2Loading,     setLevel2Loading]     = useState(false);
  const [level3Items,       setLevel3Items]       = useState([]);
  const [level3Loading,     setLevel3Loading]     = useState(false);
  const [visitors,          setVisitors]          = useState([]);
  const [visitorsLoading,   setVisitorsLoading]   = useState(false);
  const [visitorSearch,     setVisitorSearch]     = useState('');
  const [visitorSort,       setVisitorSort]       = useState('name'); // 'name' | 'recent'

  // ── Gallery ───────────────────────────────────────────────────────────────
  const [activeTab,     setActiveTab]     = useState('matched');
  const [photos,        setPhotos]        = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosTotal,   setPhotosTotal]   = useState(0);
  const [photoStats,    setPhotoStats]    = useState({ total_all: 0, total_matched: 0, total_rejected: 0, total_no_face: 0 });
  // photosPage tracked via pageRef (no re-render needed for pagination)
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [imgSize,       setImgSize]       = useState({});

  // Refs to avoid stale-closure races during pagination
  const loadingRef   = useRef(false);
  const pageRef      = useRef(1);
  const photoAreaRef = useRef(null); // scroll container

  // ── Match run controls ────────────────────────────────────────────────────
  const [threshold,    setThreshold]    = useState(45);
  const [faceConfMin,  setFaceConfMin]  = useState(90); // 0–100 (sent as /100) — retinaface is well-calibrated at 0.9+
  const [runTarget,    setRunTarget]    = useState('visitor'); // 'visitor' | 'all'
  const [runStatus,  setRunStatus]  = useState(null);
  const [runId,      setRunId]      = useState(null);
  const runPollRef       = useRef(null);
  const embeddingPollRef = useRef(null);
  const [embeddingReady, setEmbeddingReady] = useState(true); // false while polling after selfie replace

  // ── Share / invite ────────────────────────────────────────────────────────
  const [galleryUrl,     setGalleryUrl]     = useState('');
  const [sendingGallery, setSendingGallery] = useState(false);
  const [galleryMsg,     setGalleryMsg]     = useState('');
  const [regPhone,       setRegPhone]       = useState('');
  const [sendingReg,     setSendingReg]     = useState(false);
  const [regMsg,         setRegMsg]         = useState('');

  // ── Load dealers on mount ─────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/superadmin/dealers`)
      .then(r => setDealers(r.data))
      .catch(console.error)
      .finally(() => setDealersLoading(false));
  }, []);

  // ── Level 2: load when dealer or mode changes ─────────────────────────────
  useEffect(() => {
    if (!selectedDealer) { setLevel2Items([]); return; }
    setLevel2Items([]); setLevel2Loading(true);
    const url = dealerEventsMode
      ? `${API}/api/superadmin/investigate/dealers/${selectedDealer.id}/events`
      : `${API}/api/superadmin/investigate/dealers/${selectedDealer.id}/photographers`;
    axios.get(url)
      .then(r => setLevel2Items(r.data))
      .catch(console.error)
      .finally(() => setLevel2Loading(false));
  }, [selectedDealer, dealerEventsMode]);

  // ── Level 3: photographer events (non-dealer-events mode only) ────────────
  useEffect(() => {
    if (!selectedPhotographer || dealerEventsMode) { setLevel3Items([]); return; }
    setLevel3Items([]); setLevel3Loading(true);
    axios.get(`${API}/api/superadmin/investigate/photographers/${selectedPhotographer.id}/events`)
      .then(r => setLevel3Items(r.data))
      .catch(console.error)
      .finally(() => setLevel3Loading(false));
  }, [selectedPhotographer, dealerEventsMode]);

  // ── Visitors: load when event changes ────────────────────────────────────
  useEffect(() => {
    if (!selectedEvent) { setVisitors([]); return; }
    setVisitorSearch('');
    setVisitorSort('name');
    setVisitorsLoading(true);
    axios.get(`${API}/api/superadmin/investigate/events/${selectedEvent.id}/visitors`)
      .then(r => setVisitors(r.data))
      .catch(console.error)
      .finally(() => setVisitorsLoading(false));
  }, [selectedEvent]);

  // ── Gallery URL: populate when visitor has token ──────────────────────────
  useEffect(() => {
    if (!selectedVisitor) { setGalleryUrl(''); return; }
    if (selectedVisitor.gallery_token) {
      setGalleryUrl(`${API.replace('/api', '') || ''}/em/gallery?token=${selectedVisitor.gallery_token}`);
    } else {
      setGalleryUrl('');
    }
  }, [selectedVisitor]);

  // ── Photo stats: load when visitor changes ────────────────────────────────
  useEffect(() => {
    if (!selectedVisitor) { setPhotoStats({ total_all: 0, total_matched: 0, total_rejected: 0, total_no_face: 0 }); return; }
    axios.get(`${API}/api/superadmin/investigate/visitors/${selectedVisitor.id}/photo-stats`)
      .then(r => setPhotoStats(r.data))
      .catch(console.error);
  }, [selectedVisitor?.id]); // eslint-disable-line

  // ── fetchPhotos — defined before effects that call it ────────────────────
  const fetchPhotos = useCallback(async (page, reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setPhotosLoading(true);
    try {
      let url;
      if (activeTab === 'matched') {
        url = `${API}/api/superadmin/investigate/visitors/${selectedVisitor.id}/matched-photos?page=${page}&limit=20`;
      } else if (activeTab === 'all') {
        url = `${API}/api/superadmin/investigate/events/${selectedVisitor.event_id}/all-photos?page=${page}&limit=20`;
      } else if (activeTab === 'rejected') {
        url = `${API}/api/superadmin/investigate/visitors/${selectedVisitor.id}/reverse-photos?category=rejected&page=${page}&limit=20`;
      } else {
        url = `${API}/api/superadmin/investigate/visitors/${selectedVisitor.id}/reverse-photos?category=no_face&page=${page}&limit=20`;
      }
      const r = await axios.get(url);
      setPhotosTotal(r.data.total);
      setPhotos(prev => reset ? r.data.photos : [...prev, ...r.data.photos]);
      pageRef.current = page;
    } catch (e) {
      console.error(e);
    } finally {
      loadingRef.current = false;
      setPhotosLoading(false);
    }
  }, [selectedVisitor, activeTab]); // eslint-disable-line

  // ── Gallery photos: reset when visitor or tab changes ────────────────────
  useEffect(() => {
    if (!selectedVisitor) { setPhotos([]); setPhotosTotal(0); return; }
    setPhotos([]);
    setPhotosTotal(0);
    pageRef.current    = 1;
    loadingRef.current = false;
    fetchPhotos(1, true);
  }, [selectedVisitor?.id, activeTab]); // eslint-disable-line

  // ── Scroll-based lazy load next page ─────────────────────────────────────
  useEffect(() => {
    const container = photoAreaRef.current;
    if (!container || !selectedVisitor) return;
    const handleScroll = () => {
      if (loadingRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 300) {
        setPhotos(curr => {
          if (curr.length < photosTotal) fetchPhotos(pageRef.current + 1);
          return curr;
        });
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedVisitor?.id, activeTab, photosTotal, fetchPhotos]); // eslint-disable-line

  // ── Run match polling ─────────────────────────────────────────────────────
  const startRunPoll = (id) => {
    clearInterval(runPollRef.current);
    runPollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/superadmin/investigate/runs/${id}/status`);
        setRunStatus(r.data);
        if (['completed', 'failed'].includes(r.data.status)) {
          clearInterval(runPollRef.current);
          if (r.data.status === 'completed') {
            // Refresh visitors list
            const vr = await axios.get(`${API}/api/superadmin/investigate/events/${selectedEvent.id}/visitors`);
            setVisitors(vr.data);
            const updated = vr.data.find(v => v.id === selectedVisitor?.id);
            if (updated) setSelectedVisitor(u => ({ ...u, match_count: updated.match_count }));
            // Refresh stats equation bar
            axios.get(`${API}/api/superadmin/investigate/visitors/${selectedVisitor.id}/photo-stats`)
              .then(sr => setPhotoStats(sr.data)).catch(console.error);
            // Refresh photos
            loadingRef.current = false;
            fetchPhotos(1, true);
          }
        }
      } catch (e) { console.error(e); }
    }, 2500);
  };

  useEffect(() => () => {
    clearInterval(runPollRef.current);
    clearInterval(embeddingPollRef.current);
  }, []);

  // ── Escape key: close lightbox ────────────────────────────────────────────
  useEffect(() => {
    if (!lightboxPhoto) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxPhoto(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxPhoto]);

  // ── Selection handlers ────────────────────────────────────────────────────
  const selectDealer = (d) => {
    setSelectedDealer(d);
    setSelectedPhotographer(null);
    setSelectedEvent(null);
    setSelectedVisitor(null);
    setDealerEventsMode(false);
    setRunStatus(null);
    setGalleryMsg(''); setRegMsg('');
  };

  const toggleDealerEventsMode = () => {
    setDealerEventsMode(v => !v);
    setSelectedPhotographer(null);
    setSelectedEvent(null);
    setSelectedVisitor(null);
    setRunStatus(null);
  };

  const selectLevel2 = (item) => {
    if (dealerEventsMode) {
      setSelectedPhotographer(null);
      setSelectedEvent(item);
      setSelectedVisitor(null);
    } else {
      setSelectedPhotographer(item);
      setSelectedEvent(null);
      setSelectedVisitor(null);
    }
    setRunStatus(null);
    setGalleryMsg(''); setRegMsg('');
  };

  const selectEvent = (ev) => {
    setSelectedEvent(ev);
    setSelectedVisitor(null);
    setRunStatus(null);
    setGalleryMsg(''); setRegMsg('');
  };

  const selectVisitor = (v) => {
    setSelectedVisitor({ ...v, event_id: selectedEvent?.id });
    setActiveTab('matched');
    setRunStatus(null); setRunId(null);
    setGalleryMsg(''); setRegMsg(''); setRegPhone('');
    setPhotoMatchMap({});
    setPhotoStats({ total_all: 0, total_matched: 0, total_rejected: 0, total_no_face: 0 });
    setSelfieFile(null); setSelfiePreview(null); setSelfieMsg('');
    setEmbeddingReady(true);
    clearInterval(embeddingPollRef.current);
  };

  // ── Test match a single photo against visitor selfie ─────────────────────
  const handleTestMatch = async (e, photo) => {
    e.stopPropagation();
    if (photoMatchMap[photo.id]?.loading) return;
    setPhotoMatchMap(m => ({ ...m, [photo.id]: { loading: true } }));
    try {
      const r = await axios.post(
        `${API}/api/superadmin/investigate/photos/${photo.id}/match-visitor/${selectedVisitor.id}`
      );
      setPhotoMatchMap(m => ({ ...m, [photo.id]: { loading: false, best_score: r.data.best_score, face_count: r.data.face_count } }));
    } catch (err) {
      const msg = err.response?.data?.error || 'Error';
      setPhotoMatchMap(m => ({ ...m, [photo.id]: { loading: false, error: msg } }));
    }
  };

  // ── Replace visitor selfie ────────────────────────────────────────────────
  const handleSelfieFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelfieFile(file);
    setSelfiePreview(URL.createObjectURL(file));
    setSelfieMsg('');
  };

  const startEmbeddingPoll = (visitorId) => {
    clearInterval(embeddingPollRef.current);
    setEmbeddingReady(false);
    let attempts = 0;
    const MAX_ATTEMPTS = 10; // 10 × 3s = 30 seconds — enough for success; failure exits in <5s
    embeddingPollRef.current = setInterval(async () => {
      attempts++;
      try {
        const r = await axios.get(`${API}/api/superadmin/investigate/visitors/${visitorId}/embedding-status`);
        if (r.data.has_embedding) {
          clearInterval(embeddingPollRef.current);
          setEmbeddingReady(true);
          setSelfieMsg('✓ Embedding ready — you can now run match.');
          return;
        }
      } catch { /* ignore poll errors */ }
      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(embeddingPollRef.current);
        setEmbeddingReady(true); // re-enable so user isn't stuck
        setSelfieMsg('✗ No face detected in the uploaded image. Please upload a clear, front-facing photo and try again.');
      }
    }, 3000);
  };

  const handleReplaceSelfie = async () => {
    if (!selfieFile) return;
    setSelfieUploading(true); setSelfieMsg('');
    try {
      const formData = new FormData();
      formData.append('selfie', selfieFile);
      await axios.post(
        `${API}/api/superadmin/investigate/visitors/${selectedVisitor.id}/replace-selfie`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setSelfieFile(null); setSelfiePreview(null);
      if (selfieInputRef.current) selfieInputRef.current.value = '';
      setSelfieImgKey(k => k + 1); // force selfie img reload
      setSelfieMsg('⏳ Computing embedding…');
      startEmbeddingPoll(selectedVisitor.id);
    } catch (err) {
      setSelfieMsg(err.response?.data?.error || '✗ Upload failed.');
    } finally {
      setSelfieUploading(false);
    }
  };

  // ── Match run ─────────────────────────────────────────────────────────────
  const handleRunMatch = async () => {
    try {
      let r;
      const matchParams = { threshold: threshold / 100, face_conf_min: faceConfMin / 100 };
      if (runTarget === 'visitor') {
        r = await axios.post(
          `${API}/api/superadmin/investigate/visitors/${selectedVisitor.id}/run-match`,
          matchParams
        );
      } else {
        r = await axios.post(
          `${API}/api/superadmin/events/${selectedEvent.id}/trigger-match`,
          matchParams
        );
      }
      setRunId(r.data.run_id);
      setRunStatus({ status: 'running' });
      startRunPoll(r.data.run_id);
    } catch (e) {
      setRunStatus({ status: 'failed', error_message: e.response?.data?.error || 'Failed to start matching.' });
    }
  };

  // ── Generate gallery URL ──────────────────────────────────────────────────
  const handleGenerateGallery = async () => {
    try {
      const r = await axios.post(
        `${API}/api/superadmin/investigate/visitors/${selectedVisitor.id}/generate-gallery`
      );
      setGalleryUrl(r.data.gallery_url);
      setSelectedVisitor(v => ({ ...v, gallery_token: r.data.gallery_token }));
      setVisitors(vs => vs.map(v => v.id === selectedVisitor.id
        ? { ...v, gallery_token: r.data.gallery_token } : v));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to generate URL.');
    }
  };

  // ── WhatsApp: visitor list quick-send buttons ────────────────────────────
  const [sendingVisitorId,       setSendingVisitorId]       = useState(null); // gallery send
  const [selfieReminderVisitorId, setSelfieReminderVisitorId] = useState(null);
  const [viewReminderVisitorId,   setViewReminderVisitorId]   = useState(null);

  const handleSendVisitorGallery = async (visitor) => {
    setSendingVisitorId(visitor.id);
    try {
      await axios.post(`${API}/api/superadmin/investigate/visitors/${visitor.id}/send-gallery-whatsapp`);
    } catch (e) { console.error(e); } finally { setSendingVisitorId(null); }
  };

  const handleSendSelfieReminder = async (visitor) => {
    setSelfieReminderVisitorId(visitor.id);
    try {
      await axios.post(`${API}/api/superadmin/investigate/visitors/${visitor.id}/send-selfie-reminder`);
    } catch (e) { console.error(e); } finally { setSelfieReminderVisitorId(null); }
  };

  const handleSendViewReminder = async (visitor) => {
    setViewReminderVisitorId(visitor.id);
    try {
      await axios.post(`${API}/api/superadmin/investigate/visitors/${visitor.id}/send-view-reminder`);
    } catch (e) { console.error(e); } finally { setViewReminderVisitorId(null); }
  };

  // ── WhatsApp: send gallery ────────────────────────────────────────────────
  const handleSendGallery = async () => {
    setSendingGallery(true); setGalleryMsg('');
    try {
      await axios.post(
        `${API}/api/superadmin/investigate/visitors/${selectedVisitor.id}/send-gallery-whatsapp`
      );
      setGalleryMsg(`✓ Sent to ${selectedVisitor.mobile_number}`);
    } catch (e) {
      setGalleryMsg(e.response?.data?.error || '✗ Failed to send.');
    } finally {
      setSendingGallery(false);
    }
  };

  // ── WhatsApp: send registration URL ──────────────────────────────────────
  const handleSendReg = async () => {
    if (!regPhone.trim()) return;
    setSendingReg(true); setRegMsg('');
    try {
      await axios.post(
        `${API}/api/superadmin/investigate/events/${selectedEvent.id}/send-registration`,
        { phone: regPhone }
      );
      setRegMsg(`✓ Sent to ${regPhone}`);
    } catch (e) {
      setRegMsg(e.response?.data?.error || '✗ Failed to send.');
    } finally {
      setSendingReg(false);
    }
  };

  // ── Per-photo test-match results ──────────────────────────────────────────
  const [photoMatchMap, setPhotoMatchMap] = useState({}); // photo_id → {loading, best_score, error}

  // ── Replace selfie ────────────────────────────────────────────────────────
  const [selfieFile,      setSelfieFile]      = useState(null);
  const [selfiePreview,   setSelfiePreview]   = useState(null);
  const [selfieUploading, setSelfieUploading] = useState(false);
  const [selfieMsg,       setSelfieMsg]       = useState('');
  const [selfieImgKey,    setSelfieImgKey]    = useState(0); // incremented to force selfie img reload
  const selfieInputRef = useRef(null);

  // ── Sidebar collapsed state ───────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // ── Panel visibility (selection phase only) ───────────────────────────────
  const showLevel2   = !!selectedDealer;
  const showLevel3   = !dealerEventsMode && !!selectedPhotographer;
  const showVisitors = !!selectedEvent;
  const isRunning    = runStatus?.status === 'running';

  // ── Context label for gallery header ─────────────────────────────────────
  const contextChain = [
    selectedDealer?.name,
    selectedPhotographer?.name,
    selectedEvent?.name,
  ].filter(Boolean).join(' › ');

  // ─────────────────────────────────────────────────────────────────────────
  // PHASE 1: Drill-down selection (no visitor selected yet)
  // PHASE 2: Full-screen gallery (visitor selected)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Face bounding-box helpers ────────────────────────────────────────────
  // Thumbnails use height:auto (uniform scale, no offset).
  // Lightbox uses objectFit:contain (uniform scale, letterbox offset).
  function computeFaceBox(natW, natH, dspW, dspH, fx, fy, fw, fh, contain = false) {
    const scale = contain ? Math.min(dspW / natW, dspH / natH) : dspW / natW;
    const offX  = contain ? (dspW - natW * scale) / 2 : 0;
    const offY  = contain ? (dspH - natH * scale) / 2 : 0;
    return { left: fx * scale + offX, top: fy * scale + offY, w: fw * scale, h: fh * scale };
  }

  const handleImgLoad = (e, photoId, key) => {
    const img = e.target;
    setImgSize(prev => ({
      ...prev,
      [key || photoId]: { natW: img.naturalWidth, natH: img.naturalHeight, dspW: img.clientWidth, dspH: img.clientHeight }
    }));
  };

  if (selectedVisitor) {
    // ══════════════════════════════════════════════════════════════════════
    // PHASE 2 — Full-screen gallery view
    // ══════════════════════════════════════════════════════════════════════
    return (
      <div style={S.page}>

        {/* Context strip */}
        <div style={S.ctxStrip}>
          <button style={S.backBtn} onClick={() => setSelectedVisitor(null)}>
            ← Visitors
          </button>
          <div style={S.ctxChain}>{contextChain}</div>
          <img
            key={selfieImgKey}
            src={`${API}/api/photos/selfie/${selectedVisitor.id}?t=${selfieImgKey}`}
            style={S.ctxSelfie}
            alt=""
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div style={S.ctxVisitor}>
            <span style={S.ctxName}>{selectedVisitor.full_name}</span>
            <span style={S.ctxMeta}>{selectedVisitor.mobile_number} · <strong>{selectedVisitor.match_count}</strong> matched</span>
          </div>
          <div style={{ flex: 1 }} />
          {/* Tabs inline in context strip */}
          {[
            ['matched',     'Matched',     photoStats.total_matched],
            ['rejected',    'Rejected',    photoStats.total_rejected],
            ['not_indexed', 'Not Indexed', photoStats.total_no_face],
            ['all',         'All',         photoStats.total_all],
          ].map(([k, label, count]) => (
            <button
              key={k}
              style={{ ...S.ctxTab, ...(activeTab === k ? S.ctxTabActive : {}) }}
              onClick={() => setActiveTab(k)}
            >
              {label} <span style={S.ctxTabCount}>{count}</span>
            </button>
          ))}
          <button
            style={{ ...S.sideToggleBtn, ...(sidebarOpen ? S.sideToggleBtnActive : {}) }}
            onClick={() => setSidebarOpen(v => !v)}
            title={sidebarOpen ? 'Hide actions' : 'Show actions'}
          >
            {sidebarOpen ? '⚙ Hide' : '⚙ Actions'}
          </button>
        </div>

        {/* Stats equation bar */}
        <div style={S.statsBar}>
          <span style={S.statChip('#4ade80')}>{photoStats.total_matched} matched</span>
          <span style={S.statOp}>+</span>
          <span style={S.statChip('#fb923c')}>{photoStats.total_rejected} rejected</span>
          <span style={S.statOp}>+</span>
          <span style={S.statChip('#94a3b8')}>{photoStats.total_no_face} not indexed</span>
          <span style={S.statOp}>=</span>
          <span style={S.statTotal}>{photoStats.total_all} total photos</span>
        </div>

        {/* Tab-specific banners */}
        {activeTab === 'rejected' && (
          <div style={S.modeBanner}>
            Faces were detected and indexed in these photos but all scored <strong>below threshold</strong> for {selectedVisitor.full_name}. Lower the threshold or use ≈ Test Match to check scores.
          </div>
        )}
        {activeTab === 'not_indexed' && (
          <div style={{ ...S.modeBanner, background: 'rgba(148,163,184,0.07)', borderColor: 'rgba(148,163,184,0.25)', color: '#94a3b8' }}>
            These photos have <strong>no indexed faces</strong> — the algorithm never processed them. May contain visitor's face if uploaded after indexing or if face detection failed.
          </div>
        )}
        {activeTab === 'all' && (
          <div style={{ ...S.modeBanner, background: 'rgba(99,102,241,0.07)', borderColor: 'rgba(99,102,241,0.25)', color: '#818cf8' }}>
            All {photoStats.total_all} photos in this event. Face count per photo shown.
          </div>
        )}

        {/* Main: photo grid + collapsible sidebar */}
        <div style={S.galleryMain}>

          {/* Photo grid */}
          <div style={S.photoArea} ref={photoAreaRef}>
            <div style={S.photoGrid}>
              {photos.map(p => {
                const tm = photoMatchMap[p.id];
                return (
                  <div key={p.id} style={S.photoCard} onClick={() => setLightboxPhoto(p)}>
                    <img
                      src={`${API}/api/photos/file/${p.id}`}
                      style={S.photoThumb}
                      alt={p.file_name}
                      loading="lazy"
                      onLoad={e => handleImgLoad(e, p.id)}
                    />
                    {/* Face bounding box — matched tab only, when coordinates available */}
                    {activeTab === 'matched' && p.face_x != null && imgSize[p.id] && (() => {
                      const { natW, natH, dspW, dspH } = imgSize[p.id];
                      const box = computeFaceBox(natW, natH, dspW, dspH, p.face_x, p.face_y, p.face_w, p.face_h, false);
                      const visible = box.left < dspW && box.top < dspH && box.left + box.w > 0 && box.top + box.h > 0;
                      if (!visible) return null;
                      const bx = Math.max(0, box.left), by = Math.max(0, box.top);
                      const bw = Math.min(box.w, dspW - bx),  bh = Math.min(box.h, dspH - by);
                      return (
                        <>
                          <div style={{ position:'absolute', left:bx, top:by, width:bw, height:bh, border:'2px solid #4ade80', boxSizing:'border-box', borderRadius:'2px', pointerEvents:'none' }} />
                          {by > 16 && <div style={{ position:'absolute', left:bx, top:by - 17, background:'#4ade80', color:'#000', fontSize:'0.58rem', fontWeight:'800', padding:'1px 4px', borderRadius:'2px 2px 0 0', pointerEvents:'none', lineHeight:1.4 }}>{Math.round(p.confidence_score * 100)}%</div>}
                        </>
                      );
                    })()}
                    {activeTab === 'matched' && p.confidence_score != null && (
                      <div style={S.photoScore}>{Math.round(p.confidence_score * 100)}%</div>
                    )}
                    {activeTab === 'all' && p.face_count > 0 && (
                      <div style={S.photoFaceBadge}>{p.face_count} faces</div>
                    )}
                    {activeTab === 'rejected' && (
                      <div style={S.badgeRejected}>{p.face_count} faces · below threshold</div>
                    )}
                    {activeTab === 'not_indexed' && (
                      <div style={S.badgeNoFace}>not indexed</div>
                    )}
                    {/* Test match button */}
                    <button
                      style={S.testBtn}
                      onClick={e => handleTestMatch(e, p)}
                      title="Test match this photo against visitor selfie"
                    >
                      {tm?.loading ? '…' : '≈'}
                    </button>
                    {/* Test match result */}
                    {tm && !tm.loading && (
                      <div style={{
                        ...S.testResult,
                        ...(tm.error
                          ? S.testResultWarn
                          : tm.best_score >= threshold / 100
                            ? S.testResultHit
                            : S.testResultMiss)
                      }}>
                        {tm.error ? '!' : `${Math.round(tm.best_score * 100)}%`}
                      </div>
                    )}
                  </div>
                );
              })}
              {photos.length === 0 && !photosLoading && (
                <div style={S.emptyGrid}>
                  {activeTab === 'matched'     && 'No matched photos yet. Run face matching to populate results.'}
                  {activeTab === 'all'         && 'No photos uploaded for this event.'}
                  {activeTab === 'rejected'    && 'No rejected photos — every indexed face either matched or was not indexed.'}
                  {activeTab === 'not_indexed' && 'All photos in this event have indexed faces — nothing was skipped.'}
                </div>
              )}
            </div>
            {photosLoading && <div style={S.loadingMore}>Loading photos…</div>}
            {!photosLoading && photos.length > 0 && photos.length >= photosTotal && (
              <div style={S.allLoaded}>— {photosTotal} of {photosTotal} —</div>
            )}
          </div>

          {/* Collapsible sidebar */}
          {sidebarOpen && (
            <div style={S.sidebar}>

              <SideSection title="Run Face Matching">
                <div style={S.modelInfo}>
                  ArcFace · OpenCV · Cosine similarity
                </div>
                <div style={S.thresholdLabelRow}>
                  Match Threshold: <strong style={{ color: '#a78bfa' }}> {threshold}%</strong>
                  <span style={S.thresholdHint}> ({(threshold / 100).toFixed(2)})</span>
                </div>
                <input
                  type="range" min="30" max="95" step="5"
                  value={threshold}
                  onChange={e => setThreshold(Number(e.target.value))}
                  style={S.slider}
                />
                <div style={S.sliderMarks}>
                  <span>30%</span><span>95%</span>
                </div>
                <div style={S.presetRow}>
                  {[40, 45, 50, 60].map(p => (
                    <button key={p} style={{ ...S.presetBtn, ...(threshold === p ? S.presetBtnActive : {}) }}
                      onClick={() => setThreshold(p)}>{p}%</button>
                  ))}
                </div>
                {threshold < 40 && (
                  <div style={S.thresholdWarn}>
                    ⚠ Below 40% significantly increases false matches. Use only for investigation, not production runs.
                  </div>
                )}

                <div style={{ ...S.thresholdLabelRow, marginTop: '0.75rem' }}>
                  Face Confidence: <strong style={{ color: '#67e8f9' }}> {faceConfMin}%</strong>
                  <span style={S.thresholdHint}> (min detection quality)</span>
                </div>
                <input
                  type="range" min="50" max="99" step="5"
                  value={faceConfMin}
                  onChange={e => setFaceConfMin(Number(e.target.value))}
                  style={{ ...S.slider, accentColor: '#67e8f9' }}
                />
                <div style={S.sliderMarks}>
                  <span>50% (loose)</span><span>99% (strict)</span>
                </div>
                <div style={S.presetRow}>
                  {[80, 85, 90, 95].map(p => (
                    <button key={p} style={{ ...S.presetBtn, ...(faceConfMin === p ? { ...S.presetBtnActive, borderColor: '#67e8f9', color: '#67e8f9' } : {}) }}
                      onClick={() => setFaceConfMin(p)}>{p}%</button>
                  ))}
                </div>
                <div style={S.confHint}>
                  RetinaFace confidence is calibrated: 90%+ = definite face. Filters out crowd/background false detections.
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={S.radioLabel}>
                    <input type="radio" name="rt" value="visitor"
                      checked={runTarget === 'visitor'} onChange={() => setRunTarget('visitor')} />
                    {' '}This visitor only
                  </label>
                  <label style={S.radioLabel}>
                    <input type="radio" name="rt" value="all"
                      checked={runTarget === 'all'} onChange={() => setRunTarget('all')} />
                    {' '}All visitors in event
                  </label>
                </div>
                {runStatus && (
                  <div style={{
                    ...S.runStatusBox,
                    ...(runStatus.status === 'completed' ? S.runDone    : {}),
                    ...(runStatus.status === 'failed'    ? S.runFailed  : {}),
                    ...(runStatus.status === 'running'   ? S.runRunning : {}),
                  }}>
                    {runStatus.status === 'running'   && '⏳ Matching in progress…'}
                    {runStatus.status === 'completed' && `✓ Done · ${runStatus.total_matches || 0} matches`}
                    {runStatus.status === 'failed'    && `✗ ${runStatus.error_message || 'Failed'}`}
                  </div>
                )}
                {!embeddingReady && (
                  <div style={S.embeddingWait}>
                    ⏳ Computing embedding… Run Match will enable automatically.
                  </div>
                )}
                <button
                  style={{ ...S.primaryBtn, ...((isRunning || !embeddingReady) ? S.btnDisabled : {}) }}
                  onClick={handleRunMatch} disabled={isRunning || !embeddingReady}
                >
                  {isRunning ? 'Running…' : !embeddingReady ? 'Waiting for embedding…' : `Run · ${threshold}% match · ${faceConfMin}% conf`}
                </button>
              </SideSection>

              <SideSection title="Gallery URL">
                {galleryUrl ? (
                  <>
                    <div style={S.urlBox}>
                      <input readOnly value={galleryUrl} style={S.urlInput}
                        onClick={e => e.target.select()} />
                      <button style={S.copyBtn}
                        onClick={() => navigator.clipboard.writeText(galleryUrl)}>
                        Copy
                      </button>
                    </div>
                    <button
                      style={{ ...S.whatsappBtn, ...(sendingGallery ? S.btnDisabled : {}) }}
                      onClick={handleSendGallery} disabled={sendingGallery}
                    >
                      {sendingGallery ? 'Sending…' : 'Send via WhatsApp'}
                    </button>
                    {galleryMsg && (
                      <div style={{ ...S.feedbackMsg, ...(galleryMsg.startsWith('✓') ? S.feedbackOk : S.feedbackErr) }}>
                        {galleryMsg}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={S.noUrlNote}>No gallery URL yet.</div>
                    <button style={S.primaryBtn} onClick={handleGenerateGallery}>Generate URL</button>
                  </>
                )}
              </SideSection>

              <SideSection title="Invite to Register">
                <div style={S.noteText}>Send event registration link to any phone.</div>
                <div style={S.inputRow}>
                  <input
                    style={S.phoneInput} placeholder="10-digit phone"
                    value={regPhone}
                    onChange={e => setRegPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  />
                  <button
                    style={{ ...S.sendBtn, ...(!regPhone.trim() || sendingReg ? S.btnDisabled : {}) }}
                    onClick={handleSendReg} disabled={!regPhone.trim() || sendingReg}
                  >
                    {sendingReg ? '…' : 'Send'}
                  </button>
                </div>
                {regMsg && (
                  <div style={{ ...S.feedbackMsg, ...(regMsg.startsWith('✓') ? S.feedbackOk : S.feedbackErr) }}>
                    {regMsg}
                  </div>
                )}
              </SideSection>

              <SideSection title="Replace Selfie">
                <div style={S.noteText}>
                  Upload a new selfie photo. Embedding will recompute automatically, then re-run match.
                </div>
                {selfiePreview && (
                  <img src={selfiePreview} alt="preview" style={S.selfiePreviewImg} />
                )}
                <input
                  ref={selfieInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleSelfieFileChange}
                />
                <button style={S.primaryBtn} onClick={() => selfieInputRef.current?.click()}>
                  {selfieFile ? 'Change Photo' : 'Choose Photo'}
                </button>
                {selfieFile && (
                  <button
                    style={{ ...S.whatsappBtn, ...(selfieUploading ? S.btnDisabled : {}) }}
                    onClick={handleReplaceSelfie}
                    disabled={selfieUploading}
                  >
                    {selfieUploading ? 'Uploading…' : 'Upload & Replace'}
                  </button>
                )}
                {selfieMsg && (
                  <div style={{ ...S.feedbackMsg, ...(selfieMsg.startsWith('✓') ? S.feedbackOk : S.feedbackErr) }}>
                    {selfieMsg}
                  </div>
                )}
              </SideSection>

            </div>
          )}
        </div>

        {/* Lightbox */}
        {lightboxPhoto && (
          <div style={S.overlay} onClick={() => setLightboxPhoto(null)}>
            <div style={S.lightbox} onClick={e => e.stopPropagation()}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={`${API}/api/photos/file/${lightboxPhoto.id}`}
                  style={S.lightboxImg} alt={lightboxPhoto.file_name}
                  onLoad={e => handleImgLoad(e, lightboxPhoto.id, `lb_${lightboxPhoto.id}`)} />
                {/* Lightbox bounding box — objectFit:contain so coords scale linearly */}
                {activeTab === 'matched' && lightboxPhoto.face_x != null && imgSize[`lb_${lightboxPhoto.id}`] && (() => {
                  const { natW, natH, dspW, dspH } = imgSize[`lb_${lightboxPhoto.id}`];
                  // objectFit:contain — scale uniformly, center with letterbox
                  const box2 = computeFaceBox(natW, natH, dspW, dspH, lightboxPhoto.face_x, lightboxPhoto.face_y, lightboxPhoto.face_w, lightboxPhoto.face_h, true);
                  const bx = box2.left, by = box2.top, bw = box2.w, bh = box2.h;
                  return (
                    <>
                      <div style={{ position:'absolute', left:bx, top:by, width:bw, height:bh, border:'3px solid #4ade80', boxSizing:'border-box', borderRadius:'3px', boxShadow:'0 0 0 1px rgba(0,0,0,0.6)', pointerEvents:'none' }} />
                      <div style={{ position:'absolute', left:bx, top:Math.max(0, by - 28), background:'#4ade80', color:'#000', fontSize:'0.78rem', fontWeight:'800', padding:'2px 8px', borderRadius:'3px 3px 0 0', pointerEvents:'none' }}>
                        {Math.round(lightboxPhoto.confidence_score * 100)}% match
                      </div>
                    </>
                  );
                })()}
              </div>
              <div style={S.lightboxMeta}>
                <span>{lightboxPhoto.file_name}</span>
                {lightboxPhoto.confidence_score != null && (
                  <span> · Score: <strong>{Math.round(lightboxPhoto.confidence_score * 100)}%</strong></span>
                )}
                {lightboxPhoto.face_count > 0 && (
                  <span> · {lightboxPhoto.face_count} face{lightboxPhoto.face_count !== 1 ? 's' : ''}</span>
                )}
                {activeTab === 'matched' && lightboxPhoto.face_x != null && (
                  <span> · Face at ({lightboxPhoto.face_x},{lightboxPhoto.face_y}) {lightboxPhoto.face_w}×{lightboxPhoto.face_h}px</span>
                )}
              </div>
              <button style={S.lightboxClose} onClick={() => setLightboxPhoto(null)}>✕ Close</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 1 — Drill-down selection view
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div style={S.page}>

      <div style={S.panelsWrap}>

        {/* Panel 1 — Dealers */}
        <div style={S.panel}>
          <div style={S.panelHead}>
            <span style={S.panelTitle}>Dealers</span>
            <span style={S.panelCount}>{dealers.length}</span>
          </div>
          <div style={S.panelBody}>
            {dealersLoading
              ? <div style={S.panelMsg}>Loading…</div>
              : dealers.length === 0
                ? <div style={S.panelMsg}>No dealers found.</div>
                : dealers.map(d => (
                  <PanelItem key={d.id}
                    label={d.name}
                    meta={`#${d.id} · ${d.photographer_count || 0} photographers`}
                    sub={d.city || d.status}
                    active={selectedDealer?.id === d.id}
                    onClick={() => selectDealer(d)}
                  />
                ))
            }
          </div>
        </div>

        {/* Panel 2 — Photographers or Dealer Events */}
        {showLevel2 && (
          <div style={S.panel}>
            <div style={S.panelHead}>
              <span style={S.panelTitle}>{dealerEventsMode ? 'Dealer Events' : 'Photographers'}</span>
              <span style={S.panelCount}>{level2Items.length}</span>
            </div>
            <div style={S.panelToggle}>
              <label style={S.toggleLabel}>
                <input type="checkbox" checked={dealerEventsMode}
                  onChange={toggleDealerEventsMode} style={{ marginRight: '0.4rem' }} />
                Show dealer's own events
              </label>
            </div>
            <div style={S.panelBody}>
              {level2Loading
                ? <div style={S.panelMsg}>Loading…</div>
                : level2Items.length === 0
                  ? <div style={S.panelMsg}>{dealerEventsMode ? 'No direct events.' : 'No photographers.'}</div>
                  : level2Items.map(item => {
                    const isActive = dealerEventsMode
                      ? selectedEvent?.id === item.id
                      : selectedPhotographer?.id === item.id;
                    return (
                      <PanelItem key={item.id}
                        label={item.name}
                        meta={dealerEventsMode
                          ? `#${item.id} · ${item.visitor_count || 0} visitors · ${item.photo_count || 0} photos`
                          : `#${item.id} · ${item.event_count || 0} events`}
                        sub={dealerEventsMode ? (item.indexed_status || '') : (item.email || '')}
                        active={isActive}
                        onClick={() => selectLevel2(item)}
                      />
                    );
                  })
              }
            </div>
          </div>
        )}

        {/* Panel 3 — Photographer Events */}
        {showLevel3 && (
          <div style={S.panel}>
            <div style={S.panelHead}>
              <span style={S.panelTitle}>Events</span>
              <span style={S.panelCount}>{level3Items.length}</span>
            </div>
            <div style={S.panelBody}>
              {level3Loading
                ? <div style={S.panelMsg}>Loading…</div>
                : level3Items.length === 0
                  ? <div style={S.panelMsg}>No events found.</div>
                  : level3Items.map(ev => (
                    <PanelItem key={ev.id}
                      label={ev.name}
                      meta={`#${ev.id} · ${ev.visitor_count || 0} visitors · ${ev.photo_count || 0} photos`}
                      sub={ev.indexed_status}
                      active={selectedEvent?.id === ev.id}
                      onClick={() => selectEvent(ev)}
                    />
                  ))
              }
            </div>
          </div>
        )}

        {/* Panel 4 — Visitors */}
        {showVisitors && (
          <div style={{ ...S.panel, width: '100%', maxWidth: '360px' }}>
            <div style={S.panelHead}>
              <span style={S.panelTitle}>Visitors</span>
              <span style={S.panelCount}>
                {visitorSearch
                  ? `${visitors.filter(v => v.full_name.toLowerCase().includes(visitorSearch.toLowerCase()) || v.mobile_number.includes(visitorSearch)).length} / ${visitors.length}`
                  : visitors.length}
              </span>
            </div>
            <div style={{ padding: '0.4rem 0.75rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <input
                type="text"
                placeholder="Search name or mobile…"
                value={visitorSearch}
                onChange={e => setVisitorSearch(e.target.value)}
                style={S.searchInput}
              />
              <div style={{ display: 'flex', gap: '0.4rem', padding: '0.35rem 0' }}>
                <button
                  style={{ ...S.sortBtn, ...(visitorSort === 'name' ? S.sortBtnActive : {}) }}
                  onClick={() => setVisitorSort('name')}
                >A→Z Name</button>
                <button
                  style={{ ...S.sortBtn, ...(visitorSort === 'recent' ? S.sortBtnActive : {}) }}
                  onClick={() => setVisitorSort('recent')}
                >Latest First</button>
              </div>
            </div>
            <div style={S.panelBody}>
              {visitorsLoading
                ? <div style={S.panelMsg}>Loading…</div>
                : visitors.length === 0
                  ? <div style={S.panelMsg}>No visitors registered yet.</div>
                  : (() => {
                      const q = visitorSearch.toLowerCase().trim();
                      const sorted = [...visitors].sort((a, b) =>
                        visitorSort === 'recent'
                          ? new Date(b.registered_at) - new Date(a.registered_at)
                          : a.full_name.localeCompare(b.full_name)
                      );
                      const filtered = q
                        ? sorted.filter(v =>
                            v.full_name.toLowerCase().includes(q) ||
                            v.mobile_number.includes(visitorSearch.trim())
                          )
                        : sorted;
                      if (filtered.length === 0)
                        return <div style={S.panelMsg}>No match for "{visitorSearch}"</div>;
                      return filtered.map(v => (
                    <PanelItem key={v.id}
                      label={v.full_name}
                      meta={`#${v.id} · ${v.match_count} matched${v.gallery_token ? ' · URL ready' : ''}`}
                      sub={v.mobile_number}
                      active={false}
                      onClick={() => selectVisitor(v)}
                      img={`${API}/api/photos/selfie/${v.id}`}
                      compact
                      action={
                        <div style={{ display: 'flex', flexDirection: 'row', gap: '0.1rem' }}>
                          <button
                            disabled={sendingVisitorId === v.id}
                            onClick={() => handleSendVisitorGallery(v)}
                            title="Send gallery link via WhatsApp"
                            style={S.waIconBtn}
                          >
                            {sendingVisitorId === v.id ? '…' : '📲'}
                          </button>
                          <button
                            disabled={selfieReminderVisitorId === v.id}
                            onClick={() => handleSendSelfieReminder(v)}
                            title="Ask visitor to upload / re-upload selfie"
                            style={S.waIconBtn}
                          >
                            {selfieReminderVisitorId === v.id ? '…' : '🤳'}
                          </button>
                          <button
                            disabled={viewReminderVisitorId === v.id}
                            onClick={() => handleSendViewReminder(v)}
                            title="Remind visitor to view their photos"
                            style={S.waIconBtn}
                          >
                            {viewReminderVisitorId === v.id ? '…' : '🔔'}
                          </button>
                        </div>
                      }
                    />
                  ));
                    })()
              }
            </div>
          </div>
        )}

        {/* Empty state */}
        {!selectedDealer && (
          <div style={S.emptyState}>
            <div style={S.emptyIcon}>⚗</div>
            <div style={S.emptyTitle}>Select a dealer to begin</div>
            <div style={S.emptySub}>Drill down to a visitor to open the full-screen investigation gallery.</div>
          </div>
        )}

      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Styles
// ═════════════════════════════════════════════════════════════════════════════
const S = {
  page:         { height: '100vh', overflow: 'hidden', background: '#0d0d14', fontFamily: "'Segoe UI', system-ui, sans-serif", color: '#e2e8f0', display: 'flex', flexDirection: 'column' },

  // Nav
  nav:          { background: '#1a1a2e', padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid rgba(167,139,250,0.15)', flexShrink: 0 },
  navBrand:     { color: '#a78bfa', fontWeight: '800', fontSize: '1rem', flex: 1, letterSpacing: '-0.02em' },
  navBtn:       { background: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: 'none', borderRadius: '0.5rem', padding: '0.35rem 0.75rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600' },
  navUser:      { color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem' },
  logoutBtn:    { background: 'rgba(255,255,255,0.08)', color: '#aaa', border: 'none', borderRadius: '0.5rem', padding: '0.35rem 0.75rem', cursor: 'pointer', fontSize: '0.82rem' },

  // Breadcrumb
  breadcrumb:   { padding: '0.6rem 1.5rem', background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.1rem', flexShrink: 0 },
  bcItem:       { fontSize: '0.8rem', color: '#888', cursor: 'pointer', padding: '0.1rem 0.3rem', borderRadius: '0.25rem', transition: 'color 0.15s' },
  bcActive:     { color: '#a78bfa', cursor: 'default', fontWeight: '600' },
  bcSep:        { color: '#444', fontSize: '0.75rem' },

  // Panels container — horizontal scroll
  panelsWrap:   { display: 'flex', flex: 1, overflow: 'hidden', alignItems: 'stretch' },

  // Individual drilldown panel
  panel:        { width: '240px', minWidth: '240px', borderRight: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', background: '#111118', flexShrink: 0 },
  panelHead:    { padding: '0.75rem 1rem 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  panelTitle:   { fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#666' },
  panelCount:   { fontSize: '0.7rem', fontWeight: '700', color: '#a78bfa', background: 'rgba(167,139,250,0.12)', padding: '0.1rem 0.4rem', borderRadius: '50px' },
  panelToggle:  { padding: '0.5rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  toggleLabel:  { fontSize: '0.72rem', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  panelBody:    { flex: 1, overflowY: 'auto', padding: '0.25rem 0' },
  panelMsg:     { padding: '1rem', fontSize: '0.8rem', color: '#555', textAlign: 'center' },

  // Panel item
  pi:           { padding: '0.65rem 1rem', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'background 0.1s' },
  piCompact:    { padding: '0.3rem 0.75rem' },
  piActive:     { background: 'rgba(167,139,250,0.12)', borderLeft: '2px solid #a78bfa' },
  piName:       { fontSize: '0.85rem', fontWeight: '600', color: '#ddd', marginBottom: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  piMeta:       { fontSize: '0.72rem', color: '#666' },
  piSub:        { fontSize: '0.68rem', color: '#555', marginTop: '0.1rem' },

  // Gallery panel — takes remaining space
  galleryPanel: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 },
  galleryHeader:{ padding: '0.875rem 1.25rem', background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: '0.875rem', flexShrink: 0 },
  selfieThumb:  { width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(167,139,250,0.4)' },
  selfieThumbSm:{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(167,139,250,0.4)' },
  waIconBtn:    { background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '0.2rem', lineHeight: 1, flexShrink: 0 },
  searchInput:  { width: '100%', boxSizing: 'border-box', padding: '0.4rem 0.6rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.4rem', color: '#ddd', fontSize: '0.8rem', outline: 'none' },
  sortBtn:      { flex: 1, padding: '0.25rem 0', fontSize: '0.7rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.35rem', color: '#888', cursor: 'pointer' },
  sortBtnActive:{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.4)', color: '#a78bfa' },
  visitorName:  { fontSize: '1rem', fontWeight: '700', color: '#e2e8f0' },
  visitorMeta:  { fontSize: '0.78rem', color: '#888', marginTop: '0.1rem' },

  // Gallery main content (photo area + sidebar)
  galleryMain:  { flex: 1, display: 'flex', overflow: 'hidden' },

  // Photo area — owns the vertical scroll for the gallery
  photoArea:    { flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', minWidth: 0 },

  // Tabs
  tabs:         { display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.75rem 1rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)', background: '#0d0d14', flexShrink: 0 },
  tab:          { padding: '0.4rem 0.875rem', background: 'transparent', border: 'none', borderRadius: '0.5rem 0.5rem 0 0', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600', color: '#666', transition: 'all 0.15s' },
  tabActive:    { background: 'rgba(167,139,250,0.12)', color: '#a78bfa', borderBottom: '2px solid #a78bfa' },
  tabCount:     { fontSize: '0.7rem', color: '#777' },
  totalBadge:   { marginLeft: 'auto', fontSize: '0.72rem', color: '#555' },

  reverseBanner:{ margin: '0.75rem 1rem 0', padding: '0.6rem 0.875rem', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '0.5rem', fontSize: '0.78rem', color: '#f59e0b' },

  // Photo grid
  photoGrid:    { columns: '160px', columnGap: '3px', padding: '0.75rem' },
  photoCard:    { position: 'relative', breakInside: 'avoid', marginBottom: '3px', borderRadius: '3px', overflow: 'hidden', cursor: 'pointer', background: '#1a1a2e', display: 'block' },
  photoThumb:   { width: '100%', height: 'auto', display: 'block' },
  photoScore:   { position: 'absolute', bottom: '4px', right: '4px', background: 'rgba(0,0,0,0.75)', color: '#a78bfa', fontSize: '0.65rem', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' },
  photoFaceBadge:{ position: 'absolute', bottom: '4px', left: '4px', background: 'rgba(0,0,0,0.75)', color: '#60a5fa', fontSize: '0.65rem', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' },
  emptyGrid:    { gridColumn: '1/-1', padding: '3rem 1rem', textAlign: 'center', color: '#555', fontSize: '0.875rem' },
  loadingMore:  { textAlign: 'center', padding: '1rem', color: '#666', fontSize: '0.8rem' },
  allLoaded:    { textAlign: 'center', padding: '0.75rem', color: '#444', fontSize: '0.75rem' },

  // Sidebar
  sidebar:      { width: '240px', minWidth: '240px', borderLeft: '1px solid rgba(255,255,255,0.06)', overflowY: 'auto', background: '#111118', flexShrink: 0 },
  sideSection:  { padding: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  sideSectionTitle: { fontSize: '0.68rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#666', marginBottom: '0.75rem' },

  // Threshold slider
  thresholdLabelRow: { fontSize: '0.82rem', color: '#aaa', marginBottom: '0.4rem' },
  slider:       { width: '100%', accentColor: '#a78bfa', marginBottom: '0.2rem' },
  sliderMarks:  { display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#555', marginBottom: '0.75rem' },

  // Radio
  radioLabel:   { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#aaa', marginBottom: '0.4rem', cursor: 'pointer' },

  // Run status
  runStatusBox: { padding: '0.5rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.78rem', fontWeight: '600', marginBottom: '0.6rem' },
  runRunning:   { background: 'rgba(59,130,246,0.1)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)' },
  runDone:      { background: 'rgba(34,197,94,0.1)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.2)' },
  runFailed:    { background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' },

  // Buttons
  primaryBtn:   { width: '100%', padding: '0.55rem', background: '#a78bfa', color: '#0d0d14', border: 'none', borderRadius: '0.5rem', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', marginBottom: '0.4rem' },
  whatsappBtn:  { width: '100%', padding: '0.55rem', background: 'rgba(37,211,102,0.15)', color: '#25d366', border: '1px solid rgba(37,211,102,0.3)', borderRadius: '0.5rem', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', marginBottom: '0.4rem' },
  copyBtn:      { padding: '0.35rem 0.75rem', background: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: 'none', borderRadius: '0.4rem', fontWeight: '600', fontSize: '0.75rem', cursor: 'pointer', whiteSpace: 'nowrap' },
  sendBtn:      { padding: '0.45rem 0.75rem', background: '#a78bfa', color: '#0d0d14', border: 'none', borderRadius: '0.4rem', fontWeight: '700', fontSize: '0.75rem', cursor: 'pointer', whiteSpace: 'nowrap' },
  btnDisabled:   { opacity: 0.45, cursor: 'not-allowed' },
  embeddingWait:  { fontSize: '0.72rem', color: '#fbbf24', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '0.4rem', padding: '0.4rem 0.6rem', marginBottom: '0.5rem', lineHeight: 1.4 },
  thresholdWarn:  { fontSize: '0.7rem', color: '#fb923c', background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.2)', borderRadius: '0.4rem', padding: '0.35rem 0.5rem', margin: '0.25rem 0 0.5rem', lineHeight: 1.4 },
  confHint:       { fontSize: '0.68rem', color: '#64748b', marginTop: '0.25rem', marginBottom: '0.25rem', lineHeight: 1.4 },

  // Gallery URL box
  urlBox:       { display: 'flex', gap: '0.4rem', marginBottom: '0.5rem', alignItems: 'center' },
  urlInput:     { flex: 1, padding: '0.35rem 0.5rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.4rem', color: '#a78bfa', fontSize: '0.7rem', minWidth: 0 },
  noUrlNote:    { fontSize: '0.75rem', color: '#666', marginBottom: '0.6rem' },

  // Invite row
  noteText:     { fontSize: '0.72rem', color: '#666', marginBottom: '0.5rem' },
  inputRow:     { display: 'flex', gap: '0.4rem', alignItems: 'center' },
  phoneInput:   { flex: 1, padding: '0.45rem 0.6rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.4rem', color: '#e2e8f0', fontSize: '0.8rem', minWidth: 0 },

  // Feedback
  feedbackMsg:  { fontSize: '0.72rem', marginTop: '0.4rem', padding: '0.35rem 0.5rem', borderRadius: '0.35rem' },
  feedbackOk:   { background: 'rgba(34,197,94,0.1)', color: '#4ade80' },
  feedbackErr:  { background: 'rgba(239,68,68,0.1)', color: '#f87171' },

  // ── Phase 2: Context strip (slim top bar in gallery view) ────────────────
  ctxStrip:     { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 1rem', background: '#111118', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0, flexWrap: 'wrap' },
  backBtn:      { background: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: 'none', borderRadius: '0.4rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '700', whiteSpace: 'nowrap' },
  ctxChain:     { fontSize: '0.72rem', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '260px' },
  ctxSelfie:    { width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(167,139,250,0.4)', flexShrink: 0 },
  ctxVisitor:   { display: 'flex', flexDirection: 'column', lineHeight: 1.2 },
  ctxName:      { fontSize: '0.88rem', fontWeight: '700', color: '#e2e8f0' },
  ctxMeta:      { fontSize: '0.7rem', color: '#777' },
  ctxTab:       { padding: '0.3rem 0.65rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50px', cursor: 'pointer', fontSize: '0.73rem', fontWeight: '600', color: '#666', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.3rem' },
  ctxTabActive: { background: 'rgba(167,139,250,0.15)', borderColor: '#a78bfa', color: '#a78bfa' },
  ctxTabCount:  { fontSize: '0.65rem', opacity: 0.8 },
  sideToggleBtn:      { background: 'rgba(255,255,255,0.07)', color: '#aaa', border: 'none', borderRadius: '0.4rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap' },
  sideToggleBtnActive:{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa' },
  // Stats equation bar
  statsBar:     { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 1rem', background: '#0d0d14', borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0, flexWrap: 'wrap' },
  statChip:     (color) => ({ fontSize: '0.72rem', fontWeight: '700', color, whiteSpace: 'nowrap' }),
  statOp:       { fontSize: '0.72rem', color: '#444', fontWeight: '700' },
  statTotal:    { fontSize: '0.72rem', fontWeight: '700', color: '#a78bfa', whiteSpace: 'nowrap', marginLeft: '0.1rem' },

  modeBanner:   { margin: '0.5rem 1rem 0', padding: '0.5rem 0.875rem', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '0.5rem', fontSize: '0.78rem', color: '#f59e0b', flexShrink: 0 },
  badgeRejected:{ position: 'absolute', bottom: '4px', left: '4px', background: 'rgba(251,146,60,0.88)', color: '#fff', fontSize: '0.6rem', fontWeight: '700', padding: '2px 5px', borderRadius: '4px' },
  badgeNoFace:  { position: 'absolute', bottom: '4px', left: '4px', background: 'rgba(100,116,139,0.88)', color: '#fff', fontSize: '0.6rem', fontWeight: '700', padding: '2px 5px', borderRadius: '4px' },

  // ── Phase 1: Empty state ─────────────────────────────────────────────────
  emptyState:   { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '3rem', color: '#555' },
  emptyIcon:    { fontSize: '2.5rem', lineHeight: 1, marginBottom: '0.25rem' },
  emptyTitle:   { fontSize: '1rem', fontWeight: '700', color: '#666' },
  emptySub:     { fontSize: '0.8rem', color: '#444', textAlign: 'center', maxWidth: '280px' },

  // ── Model info & threshold presets ──────────────────────────────────────
  modelInfo:      { fontSize: '0.68rem', color: '#555', marginBottom: '0.6rem', fontFamily: 'monospace', letterSpacing: '0.02em' },
  thresholdHint:  { fontSize: '0.68rem', color: '#555' },
  presetRow:      { display: 'flex', gap: '0.3rem', marginBottom: '0.75rem' },
  presetBtn:      { flex: 1, padding: '0.25rem', background: 'rgba(255,255,255,0.05)', color: '#777', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.35rem', cursor: 'pointer', fontSize: '0.7rem', fontWeight: '600' },
  presetBtnActive:{ background: 'rgba(167,139,250,0.2)', color: '#a78bfa', borderColor: '#a78bfa' },

  // ── Test match button & result overlay on photo cards ───────────────────
  testBtn:        { position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.65)', color: '#ccc', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '0.62rem', fontWeight: '700', zIndex: 2, lineHeight: 1.4 },
  testResult:     { position: 'absolute', top: '26px', right: '4px', fontSize: '0.65rem', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', zIndex: 2 },
  testResultHit:  { background: 'rgba(34,197,94,0.88)', color: '#fff' },
  testResultMiss: { background: 'rgba(239,68,68,0.88)', color: '#fff' },
  testResultWarn: { background: 'rgba(245,158,11,0.88)', color: '#fff' },

  // ── Replace selfie ───────────────────────────────────────────────────────
  selfiePreviewImg: { width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '0.5rem', marginBottom: '0.5rem', border: '2px solid rgba(167,139,250,0.3)' },

  // Lightbox
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' },
  lightbox:     { position: 'relative', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' },
  lightboxImg:  { maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '0.5rem' },
  lightboxMeta: { color: '#aaa', fontSize: '0.82rem', textAlign: 'center' },
  lightboxClose:{ padding: '0.4rem 1.25rem', background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' },
};

export default SuperAdminInvestigate;
